// packages/shared/api-client/endpoints/roles.js
// Role-based access control (RBAC) API endpoints

import { baseApi } from "../baseApi";

/**
 * Role API endpoints
 * Provides role management, permissions, and RBAC configuration
 */
const roleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all permissions list
    getPermissions: builder.query({
      query: () => "/api/roles/permissions",
      providesTags: ["Role"],
    }),

    // Get all roles (predefined + custom)
    getRoles: builder.query({
      query: (params = {}) => ({
        url: "/api/roles",
        method: "GET",
        params, // { is_custom? }
      }),
      transformResponse: (response) => {
        // Backend returns { status: 'success', data: { roles: [...] } }
        // Transform to { status: 'success', data: [...] } for consistency
        if (response?.data?.roles) {
          return {
            ...response,
            data: response.data.roles,
          };
        }
        return response;
      },
      providesTags: (result) =>
        result?.data && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: "Role", id })),
              { type: "Role", id: "LIST" },
            ]
          : [{ type: "Role", id: "LIST" }],
    }),

    // Get specific role by ID
    getRoleById: builder.query({
      query: (id) => `/api/roles/${id}`,
      providesTags: (result, error, id) => [{ type: "Role", id }],
    }),

    // Admin: Create custom role
    createRole: builder.mutation({
      query: (roleData) => ({
        url: "/api/roles",
        method: "POST",
        body: roleData, // { name, description?, permissions }
      }),
      invalidatesTags: [{ type: "Role", id: "LIST" }],
    }),

    // Admin: Update role (custom roles only)
    updateRole: builder.mutation({
      query: ({ id, ...roleData }) => ({
        url: `/api/roles/${id}`,
        method: "PUT",
        body: roleData, // { name?, description?, permissions? }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Role", id },
        { type: "Role", id: "LIST" },
      ],
    }),

    // Admin: Delete role (custom roles only)
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `/api/roles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Role", id: "LIST" }],
    }),
  }),
});

// Export hooks for usage in components
export const useGetPermissionsQuery = roleApi.useGetPermissionsQuery;
export const useGetRolesQuery = roleApi.useGetRolesQuery;
export const useGetRoleByIdQuery = roleApi.useGetRoleByIdQuery;
export const useCreateRoleMutation = roleApi.useCreateRoleMutation;
export const useUpdateRoleMutation = roleApi.useUpdateRoleMutation;
export const useDeleteRoleMutation = roleApi.useDeleteRoleMutation;
